import { useState, useEffect } from 'react';
import { BizTab } from '../types';

interface CapConsultationDrawerProps {
  open: boolean;
  onClose: () => void;
  defaultTab?: BizTab;
  defaultNote?: string;
  defaultNeedTax?: boolean;
}

// -----------------------------------------------------------------------------
// Fixed: The original file contained escaped HTML entities (e.g. &lt; and &gt;)
// which broke the TypeScript syntax, causing “must be initialized” and
// “Unexpected ‘]’” errors. All JSX/TSX tags are now written with proper
// `<` / `>` characters, and the generic type for `revenueOptions` is now
// correctly terminated.
// -----------------------------------------------------------------------------
const revenueOptions: Record<BizTab, string[]> = {
  '9810': ['≤300万（年平台销售额）', '≤1000万（年平台销售额）', '1000万以上'],
  '0110': ['≤500万（年报关金额）', '≤2000万（年报关金额）', '2000万以上'],
  '1039': ['≤200万（年出口额）', '≤500万（年出口额）', '500万以上'],
  'A': ['≤100万', '≤500万', '500万以上'],
};

const tabLabel: Record<BizTab, string> = {
  '9810': '9810 海外仓/平台',
  '0110': '0110 一般贸易出口',
  '1039': '1039 无票出口',
  'A': 'A类自动化工具',
};

export default function CapConsultationDrawer({
  open,
  onClose,
  defaultTab = '9810',
  defaultNote = '',
  defaultNeedTax = false,
}: CapConsultationDrawerProps) {
  const [activeTab, setActiveTab] = useState<BizTab>(defaultTab);
  const [phone, setPhone] = useState('');
  const [revenue, setRevenue] = useState('');
  const [needTax, setNeedTax] = useState(defaultNeedTax);
  const [needAccounting, setNeedAccounting] = useState(false);
  const [watchService, setWatchService] = useState<'no' | 'sometimes' | 'often'>('no');
  const [note, setNote] = useState(defaultNote);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Sync default props when they change
  useEffect(() => {
    if (open) {
      setActiveTab(defaultTab);
      setNeedTax(defaultNeedTax);
      setNote(defaultNote);
    }
  }, [open, defaultTab, defaultNeedTax, defaultNote]);

  // Reset revenue when the active tab changes
  useEffect(() => {
    setRevenue('');
  }, [activeTab]);

  const handleClose = () => {
    onClose();
    // Reset submission state after the closing animation
    setTimeout(() => setSubmitted(false), 300);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Basic validation – prevent empty required fields
    if (!phone.trim() || !revenue) {
      // Optionally, you could show a toast or inline error here
      return;
    }

    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        bizType: tabLabel[activeTab],
        phone: phone.trim(),
        revenue,
        needTax: needTax ? '需要' : '不需要',
        needAccounting: needAccounting ? '需要' : '不需要',
        watchService:
          watchService === 'no' ? '不需要' : watchService === 'sometimes' ? '偶尔' : '经常',
        note,
      });

      const response = await fetch('https://readdy.ai/api/form/d6g6ocpghdq4qda702s0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });

      if (!response.ok) {
        // Throw to be caught by the catch block below
        throw new Error(`Network response was not ok (${response.status})`);
      }

      setSubmitted(true);
    } catch (err) {
      // Simple error handling – you can replace with a UI toast/message
      console.error('Form submission failed:', err);
      alert('提交失败，请稍后再试。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Overlay */}
      <div
        id="cap-consultation-overlay"
        className={`fixed inset-0 bg-black/60 z-40 transition-opacity duration-300 ${
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={handleClose}
      />

      {/* Drawer */}
      <div
        id="cap-consultation-drawer"
        className={`fixed top-0 right-0 h-full w-full md:w-[480px] bg-white shadow-2xl z-50 transform transition-transform duration-300 ${
          open ? 'translate-x-0' : 'translate-x-full'
        }`}
      >
        {/* Header */}
        <div className="h-[72px] bg-gray-50 flex items-center justify-between px-8 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-bold text-gray-800">咨询报价</h3>
            <p className="text-xs text-[#14B8A6] mt-0.5">
              当前业务类型：{tabLabel[activeTab]}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-xl text-gray-600" />
          </button>
        </div>

        {/* Content */}
        <div className="p-8 overflow-y-auto h-[calc(100%-72px-88px)]">
          {!submitted ? (
            <form
              id="cap-quote-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-6"
            >
              {/* Phone */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  手机号 <span className="text-red-500">*</span>
                </label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="请输入您的手机号"
                  className="w-full h-12 px-4 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#14B8A6] transition-colors"
                />
              </div>

              {/* Revenue */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {activeTab === '9810'
                    ? '年平台销售额区间'
                    : activeTab === '0110'
                    ? '年报关金额区间'
                    : activeTab === '1039'
                    ? '年出口额区间'
                    : '年度规模区间'}
                  <span className="text-red-500"> *</span>
                </label>
                <div className="space-y-2">
                  {revenueOptions[activeTab].map((opt) => (
                    <label key={opt} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="revenue"
                        value={opt}
                        checked={revenue === opt}
                        onChange={(e) => setRevenue(e.target.value)}
                        className="w-4 h-4 text-[#14B8A6] cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">{opt}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Need Tax */}
              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="needTax"
                    checked={needTax}
                    onChange={(e) => setNeedTax(e.target.checked)}
                    className="w-4 h-4 mt-0.5 text-[#14B8A6] cursor-pointer"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      是否需要退税服务包
                    </span>
                    <p className="text-xs text-gray-400 mt-0.5">
                      叠加项，不替代订阅费 / 免税费用
                    </p>
                  </div>
                </label>
              </div>

              {/* Need Accounting */}
              <div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="needAccounting"
                    checked={needAccounting}
                    onChange={(e) => setNeedAccounting(e.target.checked)}
                    className="w-4 h-4 mt-0.5 text-[#14B8A6] cursor-pointer"
                  />
                  <div>
                    <span className="text-sm font-medium text-gray-700">
                      是否需要核算服务包
                    </span>
                    <p className="text-xs text-gray-400 mt-0.5">
                      帮助账务清晰，叠加项
                    </p>
                  </div>
                </label>
              </div>

              {/* Watch Service */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  是否需要值守服务
                </label>
                <div className="flex items-center gap-3">
                  {[
                    { value: 'no', label: '不需要' },
                    { value: 'sometimes', label: '偶尔' },
                    { value: 'often', label: '经常' },
                  ].map((opt) => (
                    <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="watchService"
                        value={opt.value}
                        checked={watchService === opt.value}
                        onChange={() =>
                          setWatchService(opt.value as 'no' | 'sometimes' | 'often')
                        }
                        className="w-4 h-4 text-[#14B8A6] cursor-pointer"
                      />
                      <span className="text-sm text-gray-700">{opt.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Note */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  备注（选填）
                </label>
                <textarea
                  name="note"
                  value={note}
                  onChange={(e) => {
                    if (e.target.value.length <= 500) setNote(e.target.value);
                  }}
                  placeholder="请输入您的需求或问题"
                  rows={3}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:border-[#14B8A6] transition-colors resize-none"
                />
                <p className="text-xs text-gray-400 text-right mt-1">{note.length}/500</p>
              </div>
            </form>
          ) : (
            <div className="text-center py-12">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-check-line text-3xl text-green-600" />
              </div>
              <h4 className="text-xl font-bold text-gray-800 mb-2">提交成功</h4>
              <p className="text-gray-500 text-sm mb-8">
                我们已收到您的咨询，将尽快安排顾问联系您
              </p>
              <div className="bg-gray-50 rounded-xl p-6">
                <p className="text-sm text-gray-500 mb-4">扫码添加企业微信，获取更快响应</p>
                <div className="w-40 h-40 bg-white rounded-lg mx-auto flex items-center justify-center border border-gray-200">
                  <img 
                    src="https://static.readdy.ai/image/d6d1f2a32b22918380a2589cd9f5379c/19cca6571ebef100faaaee169f0d9063.png"
                    alt="企业微信二维码"
                    className="w-full h-full object-contain"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {!submitted && (
          <div className="absolute bottom-0 left-0 right-0 h-[88px] bg-gray-50 border-t border-gray-200 flex items-center px-8">
            <button
              type="submit"
              form="cap-quote-form"
              disabled={submitting}
              className="w-full h-12 bg-gradient-to-r from-[#FF6B35] to-[#FF8C42] text-white font-medium rounded-full hover:scale-105 transition-transform whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:scale-100"
            >
              {submitting ? '提交中...' : '提交咨询'}
            </button>
          </div>
        )}
      </div>
    </>
  );
}
